import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddBuildingModal from 'src/components/Modals/AddBuilding';
import {useCallback, useEffect, useMemo, useState} from 'react';
import AddUnitModal from 'src/components/Modals/AddUnit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {Apartment, Building, Room, RoomStatusEnum} from 'src/apis/autoGenerate/types';
import {getApartmentById, selectProperty} from 'src/redux/slice/propertySlice';
import {useAppSelector} from 'src/redux/hooks';
import Spin from 'src/components/Spin';
import {
  buildingServiceDelete,
  buildingServiceFind,
  roomServiceDelete,
} from 'src/apis/autoGenerate/services';
import {roomClient, RoomOccupancyFilter} from 'src/apis/roomClient';
import {
  PageHeader,
  StatusBadge,
  EmptyState,
  getOccupancyStatusVariant,
} from 'src/components/ui';
import {selectAccount} from 'src/redux/slice/accountSlice';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import message from 'src/components/Message';
import BatchImportDialog, {BatchImportResult} from 'src/components/BatchImport/BatchImport';
import {tenantServiceBulkImport} from 'src/apis/autoGenerate/services';
import {useOrgId} from 'src/hooks';
import LicenseQuotaDisplay from 'src/components/LicenseQuotaDisplay';
import LicenseWarningTooltip from 'src/components/LicenseWarningTooltip';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';

interface BuildingState extends Building {
  key: string;
}

type StatusFilter = '' | RoomOccupancyFilter;

export default function ApartmentDetail() {
  const {apartmentId} = useParams();
  const property = useAppSelector(selectProperty);
  const account = useAppSelector(selectAccount);
  const {orgId} = useOrgId();
  const {isAtLimit, quota, activeLicensesCount, refetch: refetchLicenseQuota} = useLicenseQuota();

  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [targetApartment, setTargetApartment] = useState<Apartment>();
  const [addBuilding, setAddBuilding] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [addUnit, setAddUnit] = useState(false);
  const [rooms, setRooms] = useState<Room[]>();
  const [totalRooms, setTotalRooms] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [buildings, setBuildings] = useState<BuildingState[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Room>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  const [unitActionIdAnchorEl, setUnitActionIdAnchorEl] = useState<HTMLButtonElement | null>(null);
  const unitActionOpen = Boolean(unitActionIdAnchorEl);

  const unitActionId = unitActionOpen ? 'unit-action-popover' : undefined;

  const selectedBuildingId = useMemo(() => {
    if (!selectedBuilding) return '';
    const suffix = '-building';
    return selectedBuilding.endsWith(suffix)
      ? selectedBuilding.slice(0, -suffix.length)
      : '';
  }, [selectedBuilding]);

  const hasBuildings = useMemo(() => {
    return targetApartment && buildings?.length > 0;
  }, [targetApartment, buildings]);

  const pageRoomIds = useMemo(
    () => (rooms || []).map(r => String(r.id)).filter(Boolean),
    [rooms]
  );
  const allPageSelected =
    pageRoomIds.length > 0 && pageRoomIds.every(id => selectedRoomIds.has(id));
  const somePageSelected =
    pageRoomIds.some(id => selectedRoomIds.has(id)) && !allPageSelected;

  const handleListItemClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    if (id !== selectedBuilding && page !== 1) {
      setPage(1);
    }
    setSelectedBuilding(id);
    setSelectedRoomIds(new Set());
  };

  const handleRoomActionClick = (event: React.MouseEvent<HTMLButtonElement>, unit: Room) => {
    setSelectedUnit(unit);
    setUnitActionIdAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setUnitActionIdAnchorEl(null);
  };

  function getBuildings() {
    const orgId = account?.userDetail?.organizations?.[0]?.id;
    if (!orgId) {
      console.log('Organization ID not available yet, skipping building fetch');
      return;
    }

    setLoadingBuildings(true);
    buildingServiceFind(orgId, undefined, [apartmentId as string])
      .then(buildingRes => {
        const _buildingRes =
          (buildingRes as unknown as {data: {buildings: Building[]}}).data?.buildings || [];
        if (_buildingRes.length > 0) {
          setSelectedBuilding(generateKey(_buildingRes[0].id as string));
        } else {
          setSelectedBuilding('');
        }
        setBuildings(_buildingRes.map(_ => ({..._, key: generateKey(_.id as string)})));
      })
      .finally(() => {
        setLoadingBuildings(false);
      });
  }

  const getRooms = useCallback(() => {
    if (!selectedBuildingId) return;
    setLoadingRooms(true);
    roomClient
      .find({
        buildingId: selectedBuildingId,
        pageSize: 10,
        pageNum: page,
        status: statusFilter || undefined,
      })
      .then(roomRes => {
        if (!roomRes || !roomRes.data || !Array.isArray(roomRes.data.rooms)) {
          console.error('Unexpected room response format:', roomRes);
          setRooms([]);
          setTotalRooms(0);
          return;
        }

        const _roomRes = roomRes.data.rooms as Room[];
        setRooms(_roomRes);
        setTotalRooms(
          roomRes.data.total !== undefined ? roomRes.data.total : _roomRes.length
        );
      })
      .finally(() => {
        setLoadingRooms(false);
      });
  }, [selectedBuildingId, page, statusFilter]);

  useEffect(() => {
    if (selectedBuildingId) {
      getRooms();
    }
  }, [getRooms, selectedBuildingId, page, statusFilter]);

  const handleUnitEdit = () => {
    setUnitActionIdAnchorEl(null);
    setAddUnit(true);
  };

  const handleDeleteBuilding = (id: string) => {
    buildingServiceDelete(id)
      .then(res => {
        const data =
          res && typeof res === 'object' && 'data' in res ? (res as any).data : undefined;
        if (data && typeof data === 'object' && data.success === false && data.error) {
          message.error(data.error);
        } else {
          getBuildings();
          message.success('Building deleted successfully');
        }
      })
      .catch(err => {
        console.error('Delete building error:', err);
        let msg = '';
        const apiError = err?.response?.data || err?.data || err?.err || err;
        if (apiError && typeof apiError === 'object') {
          msg = apiError.error || apiError.message || apiError.err || '';
        } else if (typeof apiError === 'string') {
          msg = apiError;
        }
        if (!msg && err?.message && typeof err.message === 'string') {
          msg = err.message;
        }
        if (!msg) {
          msg = 'Failed to delete building';
        }
        message.error(msg);
      });
  };

  const handleRemoveRoom = () => {
    roomServiceDelete(selectedUnit?.id as string)
      .then(() => {
        getRooms();
        void refetchLicenseQuota();
        message.success('Unit removed successfully');
      })
      .catch(err => {
        console.error('Delete room error:', err);
        let msg = '';
        const apiError = err?.response?.data || err?.data || err?.err || err;
        if (apiError && typeof apiError === 'object') {
          msg = apiError.error || apiError.message || apiError.err || '';
        } else if (typeof apiError === 'string') {
          msg = apiError;
        }
        if (!msg && err?.message && typeof err.message === 'string') {
          msg = err.message;
        }
        if (!msg) {
          msg = 'Failed to remove unit';
        }
        message.error(msg);
      });
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setPage(1);
    setSelectedRoomIds(new Set());
  };

  const toggleRoomSelected = (roomId: string) => {
    setSelectedRoomIds(prev => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedRoomIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const id of pageRoomIds) next.delete(id);
      } else {
        for (const id of pageRoomIds) next.add(id);
      }
      return next;
    });
  };

  const [selectingAllFiltered, setSelectingAllFiltered] = useState(false);

  /** Select every unit matching the current occupancy filter in this building. */
  const selectAllFilteredInBuilding = async () => {
    if (!selectedBuildingId || !statusFilter) return;
    setSelectingAllFiltered(true);
    try {
      const pageSize = 200;
      const ids: string[] = [];
      let pageNum = 1;
      let total = Infinity;
      while (ids.length < total) {
        const res = await roomClient.find({
          buildingId: selectedBuildingId,
          pageSize,
          pageNum,
          status: statusFilter,
        });
        total = res.data.total ?? 0;
        for (const r of res.data.rooms || []) {
          if (r.id) ids.push(String(r.id));
        }
        if (!res.data.rooms?.length || ids.length >= total) break;
        pageNum += 1;
        if (pageNum > 50) break; // hard cap: 10k units
      }
      setSelectedRoomIds(new Set(ids));
      message.success(`Selected ${new Set(ids).size} ${statusFilter} unit(s)`);
    } catch (err: any) {
      message.error(err?.message || 'Failed to select units');
    } finally {
      setSelectingAllFiltered(false);
    }
  };

  const handleBatchDelete = async () => {
    const ids = Array.from(selectedRoomIds);
    if (ids.length === 0) return;
    setBatchDeleting(true);
    try {
      const res = await roomClient.batchDelete(ids);
      const result = res.data;
      setSelectedRoomIds(new Set());
      setBatchDeleteOpen(false);
      getRooms();
      if (result.deletedCount > 0) {
        void refetchLicenseQuota();
      }
      if (result.failedCount === 0) {
        message.success(
          `Deleted ${result.deletedCount} unit${result.deletedCount === 1 ? '' : 's'}`
        );
      } else if (result.deletedCount === 0) {
        message.error(
          result.failed[0]?.message || `Failed to delete ${result.failedCount} unit(s)`
        );
      } else {
        message.warning(
          `Deleted ${result.deletedCount}, failed ${result.failedCount}. Occupied units with residents cannot be deleted.`
        );
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || err?.message || 'Batch delete failed');
    } finally {
      setBatchDeleting(false);
    }
  };

  useEffect(() => {
    if (apartmentId && account?.userDetail?.organizations?.[0]?.id) {
      getApartmentById(apartmentId).then(targetApartment => {
        setTargetApartment(targetApartment || undefined);
      });
    }
  }, [apartmentId, account?.userDetail?.organizations]);

  useEffect(() => {
    getBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetApartment, account?.userDetail?.organizations]);

  useEffect(() => {
    if (!addUnit) {
      setSelectedUnit(undefined);
    }
  }, [addUnit]);

  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<BatchImportResult | null>(null);

  return (
    <>
      <Spin spinning={property.loadingList || loadingBuildings || loadingRooms} />

      <PageHeader
        title={targetApartment?.name || 'Property Details'}
        backLink={{label: 'Back to Properties', href: '/property'}}
        actions={<LicenseQuotaDisplay variant="header-card" />}
      />

      {hasBuildings ? (
        <Grid container pt={2}>
          <Grid item xs={3}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, textTransform: 'none'}}>
                Buildings
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setEditingBuilding(null);
                  setAddBuilding(true);
                }}
                aria-label="Add building"
              >
                <AddIcon />
              </IconButton>
            </Box>
            <List
              component="nav"
              aria-label="building list"
              sx={{height: 'calc(100vh - 200px)', overflow: 'auto'}}
            >
              {buildings.map(item => {
                const isSelected = selectedBuilding === item.key;
                return (
                  <ListItemButton
                    selected={isSelected}
                    onClick={event => handleListItemClick(event, item.key)}
                    key={item.key}
                    sx={{'&:hover': {'& .building-action': {opacity: 1}}}}
                  >
                    <ListItemText
                      primary={item.name}
                      sx={{
                        color: theme =>
                          isSelected ? theme.palette.primary.main : theme.palette.secondary.main,
                      }}
                    />
                    <IconButton
                      className="building-action"
                      sx={{opacity: 0}}
                      size="small"
                      onClick={event => {
                        event.stopPropagation();
                        setEditingBuilding(item);
                        setAddBuilding(true);
                      }}
                      aria-label={`Edit building ${item.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      className="building-action"
                      sx={{opacity: 0}}
                      size="small"
                      onClick={event => {
                        event.stopPropagation();
                        handleDeleteBuilding(item.id as string);
                      }}
                      aria-label="Delete building"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                );
              })}
              {buildings.length === 0 && (
                <Box sx={{p: 2}}>
                  <EmptyState
                    title="No buildings yet"
                    description="Add a building to organize your property."
                    action={{
                      label: 'Add Building',
                      onClick: () => {
                        setEditingBuilding(null);
                        setAddBuilding(true);
                      },
                      icon: <AddIcon />,
                    }}
                  />
                </Box>
              )}
            </List>
          </Grid>
          <Grid item xs={9}>
            {selectedBuildingId ? (
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{mb: 2, gap: 2, flexWrap: 'wrap'}}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{minWidth: 160}}>
                      <InputLabel id="unit-status-filter-label">Occupancy</InputLabel>
                      <Select
                        labelId="unit-status-filter-label"
                        label="Occupancy"
                        value={statusFilter}
                        onChange={e =>
                          handleStatusFilterChange(e.target.value as StatusFilter)
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="vacant">Vacant</MenuItem>
                        <MenuItem value="occupied">Occupied</MenuItem>
                      </Select>
                    </FormControl>
                    {statusFilter && totalRooms > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        disabled={selectingAllFiltered}
                        onClick={() => void selectAllFilteredInBuilding()}
                      >
                        {selectingAllFiltered
                          ? 'Selecting…'
                          : `Select all ${statusFilter} (${totalRooms})`}
                      </Button>
                    )}
                    {selectedRoomIds.size > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setBatchDeleteOpen(true)}
                      >
                        Delete selected ({selectedRoomIds.size})
                      </Button>
                    )}
                    {selectedRoomIds.size > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setSelectedRoomIds(new Set())}
                      >
                        Clear selection
                      </Button>
                    )}
                  </Stack>
                  <Stack direction="row" sx={{gap: 2}}>
                    <LicenseWarningTooltip action="add-unit">
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddUnit(true)}
                        disabled={isAtLimit}
                        size="small"
                      >
                        Unit
                      </Button>
                    </LicenseWarningTooltip>
                    <LicenseWarningTooltip action="batch-import">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setImportResult(null);
                          setImportOpen(true);
                        }}
                        disabled={isAtLimit || !quota || activeLicensesCount === 0}
                        size="small"
                      >
                        Batch Import
                      </Button>
                    </LicenseWarningTooltip>
                  </Stack>
                </Stack>
                {rooms && rooms?.length > 0 ? (
                  <Box>
                    <Table sx={{minWidth: 650}} aria-label="units table" size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={somePageSelected}
                              checked={allPageSelected}
                              onChange={toggleSelectAllOnPage}
                              inputProps={{'aria-label': 'Select all units on this page'}}
                            />
                          </TableCell>
                          <TableCell>Unit Name</TableCell>
                          <TableCell>Floor No</TableCell>
                          <TableCell>Occupied status</TableCell>
                          <TableCell width={100}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rooms.map(_ => {
                          const id = String(_.id);
                          return (
                            <TableRow key={id} selected={selectedRoomIds.has(id)}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedRoomIds.has(id)}
                                  onChange={() => toggleRoomSelected(id)}
                                  inputProps={{'aria-label': `Select unit ${_.name}`}}
                                />
                              </TableCell>
                              <TableCell>{_.name}</TableCell>
                              <TableCell>{_.floorName}</TableCell>
                              <TableCell>
                                <StatusBadge
                                  status={getOccupancyStatusVariant(_.status)}
                                  label={
                                    _.status === RoomStatusEnum.Occupied
                                      ? 'Occupied'
                                      : _.status === RoomStatusEnum.Vacant
                                        ? 'Vacant'
                                        : 'Unknown'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton onClick={e => handleRoomActionClick(e, _)}>
                                  <MoreHorizIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Grid container justifyContent="flex-end" mt={2}>
                      <Pagination
                        count={Math.max(1, Math.ceil(totalRooms / 10))}
                        page={page}
                        onChange={handlePaginationChange}
                      />
                    </Grid>
                  </Box>
                ) : (
                  <EmptyState
                    title={statusFilter === 'vacant' ? 'No vacant units' : 'No units yet'}
                    description={
                      statusFilter === 'vacant'
                        ? 'There are no vacant units in this building.'
                        : 'Add a unit to this building.'
                    }
                    action={
                      statusFilter
                        ? {
                            label: 'Show all units',
                            onClick: () => handleStatusFilterChange(''),
                          }
                        : {
                            label: 'Unit',
                            onClick: () => setAddUnit(true),
                            icon: <AddIcon />,
                          }
                    }
                  />
                )}
              </Box>
            ) : (
              <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200}}>
                <EmptyState
                  compact
                  title="Select a building"
                  description="Choose a building from the list to view its units."
                />
              </Box>
            )}
          </Grid>
          <Popover
            id={unitActionId}
            open={unitActionOpen}
            anchorEl={unitActionIdAnchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={handleUnitEdit}>
                  <ListItemText primary="Edit Unit" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setUnitActionIdAnchorEl(null);
                    handleRemoveRoom();
                  }}
                >
                  <ListItemText primary="Remove" />
                </ListItemButton>
              </ListItem>
            </List>
          </Popover>
        </Grid>
      ) : (
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          height="calc(100vh - 120px)"
        >
          <EmptyState
            title="No buildings yet"
            description="Add a building to organize your property."
            action={{
              label: 'Add Building',
              onClick: () => {
                setEditingBuilding(null);
                setAddBuilding(true);
              },
              icon: <AddIcon />,
            }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setImportResult(null);
              setImportOpen(true);
            }}
            disabled={isAtLimit || !quota || activeLicensesCount === 0}
            sx={{mt: 1}}
          >
            Batch Import
          </Button>
        </Grid>
      )}
      {addBuilding && (
        <AddBuildingModal
          key={editingBuilding?.id || 'new'}
          open={addBuilding}
          apartmentId={targetApartment?.id as string}
          building={editingBuilding || undefined}
          setOpen={setAddBuilding}
          handleConfirm={() => {
            setAddBuilding(false);
            setEditingBuilding(null);
            getBuildings();
          }}
          handleCancel={() => {
            setAddBuilding(false);
            setEditingBuilding(null);
          }}
        />
      )}
      {addUnit && (
        <AddUnitModal
          open={addUnit}
          buildingId={selectedBuildingId}
          initData={selectedUnit}
          handleCancel={() => {
            setAddUnit(false);
          }}
          handleConfirm={() => {
            getRooms();
            void refetchLicenseQuota();
            setAddUnit(false);
          }}
        />
      )}
      <Dialog
        open={batchDeleteOpen}
        onClose={batchDeleting ? undefined : () => setBatchDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete selected units?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to delete <strong>{selectedRoomIds.size}</strong> unit
            {selectedRoomIds.size === 1 ? '' : 's'}. Units that still have residents assigned
            will be skipped. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteOpen(false)} disabled={batchDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleBatchDelete()}
            disabled={batchDeleting}
          >
            {batchDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <BatchImportDialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportResult(null);
        }}
        loading={importLoading}
        result={importResult}
        onFileSelected={async file => {
          if (!orgId) {
            message.error('Organization ID is missing');
            return;
          }
          setImportLoading(true);
          setImportResult(null);
          try {
            const formData = new FormData();
            formData.append('csvFile', file);
            formData.append('orgId', orgId);

            const response = await tenantServiceBulkImport(formData);
            const result = response.data;
            setImportResult(result);

            const importedCount = result.results?.success?.length || 0;
            const skippedCount = result.results?.errors?.length || 0;

            if (importedCount > 0) {
              getBuildings();
              getRooms();
            }

            if (importedCount > 0 && skippedCount === 0) {
              message.success(result.message || 'Batch import completed!');
            } else if (importedCount > 0) {
              message.warning(result.message || 'Batch import completed with skipped rows.');
            } else {
              message.error(result.message || 'Batch import failed!');
            }
          } catch (err: any) {
            message.error(err?.message || 'Batch import failed!');
          } finally {
            setImportLoading(false);
          }
        }}
      />
    </>
  );
}

function generateKey(id: string) {
  return `${id}-building`;
}
